<script setup>
import { reactive, computed, ref, onMounted } from 'vue'
import { PageHeading, Toggle, Select, Button, Icon, Breadcrumb, Input, Radio } from '@components'
import GeneralItem from './GeneralItem.vue'
import Fieldset from './Fieldset.vue'

// Settings store.
import { useSettings } from '@store/settings'
const $settings = useSettings()

const $options = prebook_admin.options

const updateGeneral = async () => {
    await $settings.saveSettings()

    Toast.success('Updated settings.')
}

import { isSm } from '@utils/mediaQuery'
import { duration } from 'moment/moment'

const statusOptions = [
    {
        label: 'Approved',
        value: 'approved'
    },
    {
        label: 'Pending',
        value: 'pending'
    },
]

const serviceDurationOptions = ref([
    { label: '5 min', value: '5 min' },
    { label: '10 min', value: '10 min' },
    { label: '15 min', value: '15 min' },
    { label: '20 min', value: '20 min' },
    { label: '30 min', value: '30 min' },
    { label: '45 min', value: '45 min' },
    { label: '1 h', value: '1 h' },
    // { label: '1 h 30 min', value: '1 h 30 min' },
    { label: '2 h', value: '2 h' }
])

const durationMap = ref({
    '5 min': { duration: 5, duration_unit: 'minute' },
    '10 min': { duration: 10, duration_unit: 'minute' },
    '15 min': { duration: 15, duration_unit: 'minute' },
    '20 min': { duration: 20, duration_unit: 'minute' },
    '30 min': { duration: 30, duration_unit: 'minute' },
    '45 min': { duration: 45, duration_unit: 'minute' },
    '1 h': { duration: 1, duration_unit: 'hour' },
    '1 h 30 min': { duration: 1.5, duration_unit: 'hour' },
    '2 h': { duration: 2, duration_unit: 'hour' },
})


const defaultServiceDuration = ref('')

onMounted(() => {
    defaultServiceDuration.value = `${$settings.getSettings.service?.default_duration} ${$settings.getSettings.service?.default_duration_unit == 'minute' ? 'min' : 'h'}`
})

const setDefaultServiceDuration = () => {
    let option = durationMap.value[defaultServiceDuration.value]

    $settings.getSettings.service.default_duration = option.duration
    $settings.getSettings.service.default_duration_unit = option.duration_unit
}

const timeSlotSteps = ref([
    { label: '5 min', value: 5 },
    { label: '10 min', value: 10 },
    { label: '15 min', value: 15 },
    { label: '20 min', value: 20 },
    { label: '30 min', value: 30 },
    { label: '45 min', value: 45 },
    { label: '1 h', value: 60 },
    { label: '1 h 30 min', value: 90 },
    { label: '2 h', value: 120 }
])


// const defaultAppointmentTimeSlot = ref('')

// onMounted(() => {
//     defaultAppointmentTimeSlot.value = $settings.getSettings.appointment?.default_time_slot_step
// })

// const setDefaultAppointmentTimeSlot = () => {
//     $settings.getSettings.appointment.default_time_slot_step = defaultAppointmentTimeSlot.value
// }

const slotOptions = ref([
    {
        label: `${$settings.labels.service?.singular} Duration`,
        value: 'service_duration'
    },
    {
        label: 'Time Slot',
        value: 'time_slot'
    }
])
</script>

<template>
    <div class="md:max-w-[992px] m-auto mt-[-22px]">
        <!-- breadcrumb -->
        <Breadcrumb :items="[
            { icon: true, name: 'settings01', route: { name: 'settings' } },
            { name: 'General', route: { name: 'settings.general' } }
        ]" />

        <!-- page title -->
        <PageHeading title="General" :divider="false" class="pb-9" />

        <div class="md:flex">
            <!-- left sidebar -->
            <div class="w-[256px] text-[#101828] text-base font-normal flex flex-col gap-[27px]" v-if="!isSm">
                <div class="font-semibold">{{ $settings.labels.appointment?.singular }} Settings</div>
            </div>

            <!-- right content -->
            <div class="md:w-[736px] flex flex-col gap-4 md:pl-10" :class="{ 'opacity-60 pointer-events-none': $settings.isSaving }">

                <!-- Appointments  -->

                <Fieldset>
                    Default {{ $settings.labels.appointment?.singular }} Status
                    <template #field>
                        <Select :options="statusOptions" v-model="$settings.getSettings.appointment.default_status" class="w-full"></Select>
                    </template>
                </Fieldset>

                <!-- Calendar  -->
                <Fieldset>
                    First Day Of Week
                    <template #field>
                        <Select :options="['Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']" v-model="$settings.getSettings.calendar.first_day" class="w-full"></Select>
                    </template>
                </Fieldset>
                <Fieldset>
                    Days Available for Booking in Advance
                    <template #field>
                        <Input placeholder="Available period" type="number" class="my-1.5 w-full" :min="1" :max="1000" v-model="$settings.getSettings.calendar.available_period_days" />
                    </template>
                </Fieldset>
                <Fieldset>
                    Default {{ $settings.labels.service?.singular }} Duration
                    <template #field>
                        <Select :options="serviceDurationOptions" @change="setDefaultServiceDuration()" v-model="defaultServiceDuration" class="w-full"></Select>
                    </template>
                </Fieldset>
                <Fieldset>
                    Show Slot Step As
                    <template #field>
                        <Radio :options="slotOptions" v-model="$settings.getSettings.appointment.show_slot_as" />
                    </template>
                </Fieldset>
                <Fieldset v-if="$settings.getSettings.appointment.show_slot_as == 'time_slot'">
                    Time Slot Step
                    <template #field>
                        <Select :options="timeSlotSteps" v-model="$settings.getSettings.appointment.default_time_slot_step" class="w-full"></Select>
                    </template>
                </Fieldset>
                <!-- Footer actions -->
                <div class="flex items-center gap-10">
                    <Button size="xl" type="secondary_gray" radius="square" classButton="!w-[180px] mt-10 justify-between" @click.prevent="$router.push({ name: 'settings' })">
                        <div class="w-[70%]">Back</div>
                        <template #before>
                            <Icon name="arrowNarrowLeft" size="20px"></Icon>
                        </template>

                    </Button>
                    <Button @click="updateGeneral()" size="xl" type="primary" radius="square" classButton="w-full mt-10 justify-between">
                        <div class="w-[95%]">Update</div>
                    </Button>
                </div>
            </div>
        </div>
    </div>
</template>
